
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Search, 
  Plus, 
  SortAsc, 
  Filter, 
  Share
} from "lucide-react";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { InventoryShareDialog } from "./InventoryShareDialog";
import { useAuth } from "@/components/auth/AuthProvider";
import { useIsMobile } from "@/hooks/use-mobile";

interface InventoryHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onAddItem: () => void;
  onSortChange: (sort: string) => void;
  onFilterChange: (filter: string) => void;
}

export function InventoryHeader({
  searchQuery,
  onSearchChange,
  onAddItem,
  onSortChange,
  onFilterChange,
}: InventoryHeaderProps) {
  const [showShareDialog, setShowShareDialog] = useState(false);
  const { user } = useAuth();
  const isMobile = useIsMobile();

  // Get user's inventory share data
  const { data: inventoryShareData } = useQuery({
    queryKey: ["user-inventory-share", user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from("user_inventory_shares")
        .select("public_share_token, is_public_shared, public_share_expiration, public_share_revoked")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching inventory share data:", error);
        return null;
      }

      return data ? {
        share_token: data.public_share_token,
        is_shared: data.is_public_shared && !data.public_share_revoked,
        expiration: data.public_share_expiration,
      } : null;
    },
    enabled: !!user,
  });

  return (
    <>
      <div className="space-y-4 mb-6">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/60" />
          <Input
            placeholder="Search inventory..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 bg-background/40 backdrop-blur-sm border-white/10 h-12 text-white placeholder:text-white/50 rounded-xl"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button 
            onClick={onAddItem}
            className="flex-1 h-12 text-white font-semibold rounded-xl bg-primary hover:bg-primary/90 shadow-lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Item
          </Button>
          
          <Button
            onClick={() => setShowShareDialog(true)}
            variant="outline"
            className="px-4 h-12 bg-background/40 backdrop-blur-sm border-white/10 text-white hover:bg-white/10 rounded-xl"
            disabled={!user}
          >
            <Share className="h-4 w-4" />
          </Button>
        </div>

        {/* Sort & Filter */}
        <div className="flex gap-3">
          <Select onValueChange={onSortChange} defaultValue="name_asc">
            <SelectTrigger className="flex-1 bg-background/40 backdrop-blur-sm border-white/10 h-12 text-white rounded-xl">
              <SortAsc className="h-4 w-4 mr-2 text-white/70" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background/95 backdrop-blur-md border-white/10 rounded-xl">
              <SelectItem value="name_asc">Sort & Filter</SelectItem>
              <SelectItem value="name_desc">Name Z-A</SelectItem>
              <SelectItem value="cost_asc">Cost Low-High</SelectItem>
              <SelectItem value="cost_desc">Cost High-Low</SelectItem>
              <SelectItem value="quantity_asc">Quantity Low-High</SelectItem>
              <SelectItem value="quantity_desc">Quantity High-Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {user && (
        <InventoryShareDialog
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
          userId={user.id}
          currentShareData={inventoryShareData}
        />
      )}
    </>
  );
}
